/**
 * Pure helpers for managing `dealer_photos` as a manual override on a feed-backed vehicle.
 *
 * Product rules (ISE-10):
 * - Photo source is either all-feed or all-uploaded; feed photos are never copied into the override.
 * - With no override the upload grid starts empty and the current feed photos are shown read-only.
 * - An override with zero photos is not allowed: emptying the grid means "release the override".
 *
 * Everything here is side-effect free so the Edit Vehicle page can stay thin and the state table can
 * be unit tested directly. The save-intent table is keyed on whether an ACTIVE override exists
 * (not on the pending action type) because that decides whether the backend can still release.
 */

import type { ActiveManualOverride, PendingManualOverrideAction } from '@/services/inventory'
import { areFieldValuesEquivalent } from '@/utils/fieldOverrideState'

export const DEALER_PHOTOS_FIELD = 'dealer_photos'

/** Usable photo URLs from an arbitrary value (non-array and blank/non-string entries are dropped). */
export function normalizePhotoUrlList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

type OverrideStateInput = {
  activeOverride?: Pick<ActiveManualOverride, 'manualValue'> | null
  pendingAction?: Pick<PendingManualOverrideAction, 'action' | 'manualValue'> | null
}

function isPendingAddOrUpdate(pending: OverrideStateInput['pendingAction']): boolean {
  return pending?.action === 'ADD_OVERRIDE' || pending?.action === 'UPDATE_OVERRIDE'
}

/**
 * Photos the upload grid starts with:
 * - pending ADD/UPDATE: the pending photos;
 * - pending REMOVE (release staged): empty;
 * - active override: the override photos;
 * - otherwise empty (feed photos are never copied into the grid).
 */
export function seedFeedBackedPhotoUrls(input: OverrideStateInput): string[] {
  const pending = input.pendingAction
  if (isPendingAddOrUpdate(pending)) return normalizePhotoUrlList(pending?.manualValue)
  if (pending?.action === 'REMOVE_OVERRIDE') return []
  if (input.activeOverride) return normalizePhotoUrlList(input.activeOverride.manualValue)
  return []
}

/**
 * Current feed photos to show read-only (never the override photos):
 * - active override: the release target the backend reports (valueIfReleasedNow, else currentFeedValue);
 *   unknown means none are shown;
 * - pending ADD/UPDATE without an active override: the feed value locked with the pending action;
 * - otherwise: the vehicle's own (feed) photos.
 */
export function feedPhotoUrlsForDisplay(input: {
  activeOverride?: Pick<ActiveManualOverride, 'valueIfReleasedNow' | 'currentFeedValue'> | null
  pendingAction?: Pick<PendingManualOverrideAction, 'action' | 'mappedValueAtLock'> | null
  vehicleValue?: unknown
}): string[] {
  const active = input.activeOverride
  if (active) {
    if ('valueIfReleasedNow' in active && active.valueIfReleasedNow !== undefined) {
      return normalizePhotoUrlList(active.valueIfReleasedNow)
    }
    if ('currentFeedValue' in active && active.currentFeedValue !== undefined) {
      return normalizePhotoUrlList(active.currentFeedValue)
    }
    return []
  }
  const pending = input.pendingAction
  if (isPendingAddOrUpdate(pending) && pending && 'mappedValueAtLock' in pending) {
    return normalizePhotoUrlList(pending.mappedValueAtLock)
  }
  return normalizePhotoUrlList(input.vehicleValue)
}

export type FeedBackedPhotoSaveIntent =
  /** Stage ADD/UPDATE with `manualValue` (any pending release for the field is superseded). */
  | { kind: 'stage'; manualValue: string[] }
  /** Stage REMOVE_OVERRIDE (release the active override). */
  | { kind: 'release' }
  /** Discard the pending ADD/UPDATE draft action (no active override to release). */
  | { kind: 'discard' }
  /** Nothing to stage. */
  | { kind: 'noop' }

/**
 * Decides what saving the photo grid stages for a feed-backed vehicle.
 *
 * `urls` is the final ordered list of the grid (existing URLs plus freshly uploaded URLs).
 * `releaseRequested` is the explicit release control (staged locally, not the pending draft state).
 * `hasPolicyChange` forces a non-empty unchanged grid to re-stage so the new policy is saved.
 *
 * State table (active = an ACTIVE committed override exists):
 * - release requested and active          -> release
 * - empty grid, active, pending REMOVE    -> noop (release already staged)
 * - empty grid, active                    -> release (also covers pending UPDATE over active)
 * - empty grid, no active, pending ADD/UPDATE -> discard the pending action
 * - empty grid, nothing                   -> noop
 * - non-empty, active, pending REMOVE     -> stage (UPDATE; the staged release is superseded)
 * - non-empty, unchanged vs seed          -> noop (unless the policy changed)
 * - non-empty otherwise                   -> stage (ADD without active, UPDATE with active)
 */
export function resolveFeedBackedPhotoSaveIntent(input: {
  urls: string[]
  activeOverride?: OverrideStateInput['activeOverride']
  pendingAction?: OverrideStateInput['pendingAction']
  releaseRequested?: boolean
  hasPolicyChange?: boolean
}): FeedBackedPhotoSaveIntent {
  const urls = normalizePhotoUrlList(input.urls)
  const hasActive = Boolean(input.activeOverride)
  const pendingAction = input.pendingAction ?? null
  const pendingIsRemove = pendingAction?.action === 'REMOVE_OVERRIDE'

  if (input.releaseRequested && hasActive) return { kind: 'release' }

  if (urls.length === 0) {
    if (hasActive) return pendingIsRemove ? { kind: 'noop' } : { kind: 'release' }
    if (isPendingAddOrUpdate(pendingAction)) return { kind: 'discard' }
    return { kind: 'noop' }
  }

  if (hasActive && pendingIsRemove) return { kind: 'stage', manualValue: urls }

  const seed = seedFeedBackedPhotoUrls({ activeOverride: input.activeOverride, pendingAction })
  if (!input.hasPolicyChange && areFieldValuesEquivalent(DEALER_PHOTOS_FIELD, urls, seed)) {
    return { kind: 'noop' }
  }
  return { kind: 'stage', manualValue: urls }
}
